import { Camera, Clock, MapPin, Star, Users } from 'lucide-react';
import { Link } from 'react-router';

import { StatusBadge } from '@/components/ui/status-badge';
import { JOB_STATUS_TONES } from '@/hooks/jobs';
import { formatDay, formatDuration, formatWallTime } from '@/lib/dates';
import type { EmployeeJobListItem } from '@shared/schemas/employee-portal';

/**
 * One job, as a card. **A card at every width**, like the client portal's — this
 * is the phone portal and a table that stacks is the admin fix for the admin
 * problem (FINDINGS.md §10.1, P6).
 *
 * Everything here is a display value the server resolved: `statusLabel`,
 * `serviceTypeName`, crew names. The prototype renders `deep_cleaning` as the
 * job subtitle and again in a pill on this exact screen (P25), so no slug
 * reaches this file and there is no second mapping in the portal.
 *
 * **The photo counter is on the card, not only inside the job.** That is
 * business-rules.md §3.1 surfacing one level up: uploading a photo *is*
 * completing a task, so "3 of 10 photos" is the progress bar, and a cleaner
 * should be able to see they have four jobs and one is barely started without
 * opening any of them.
 */
export function EmployeeJobCard({
  job,
  showDate = true,
}: {
  job: EmployeeJobListItem;
  /** Off on the dashboard, where every card is today by definition. */
  showDate?: boolean;
}) {
  const { photoRequiredTotal, photoRequiredCompleted } = job.progress;
  const allPhotosIn = photoRequiredTotal > 0 && photoRequiredCompleted === photoRequiredTotal;

  return (
    <li>
      <Link
        to={`/employee/jobs/${job.id}`}
        className="block rounded-lg border border-ink-300 bg-white p-4 shadow-card hover:border-honey-500"
      >
        <div className="flex flex-wrap items-start justify-between gap-2">
          <div className="min-w-0">
            <p className="text-h3 text-ink-900">{job.clientName}</p>
            <p className="mt-0.5 text-meta text-ink-500">
              {job.jobNumber} · {job.serviceTypeName}
            </p>
          </div>
          <div className="flex shrink-0 items-center gap-1.5">
            {job.isHighPriority ? (
              <span className="inline-flex items-center gap-1 text-meta text-status-priority-text">
                <Star aria-hidden="true" className="size-3.5 fill-current" />
                Priority
              </span>
            ) : null}
            <StatusBadge label={job.statusLabel} tone={JOB_STATUS_TONES[job.status]} />
          </div>
        </div>

        <dl className="mt-3 space-y-1.5 text-body text-ink-700">
          <div className="flex items-start gap-2">
            <dt className="sr-only">When</dt>
            <Clock aria-hidden="true" className="mt-0.5 size-4 shrink-0 text-ink-500" />
            <dd>
              {showDate ? `${formatDay(job.scheduledDate)}, ` : ''}
              {formatWallTime(job.scheduledTime)} · {formatDuration(job.estimatedDurationMinutes)}
            </dd>
          </div>

          <div className="flex items-start gap-2">
            <dt className="sr-only">Where</dt>
            <MapPin aria-hidden="true" className="mt-0.5 size-4 shrink-0 text-ink-500" />
            <dd className="min-w-0">
              {job.propertyNickname ? (
                <span className="font-medium text-ink-900">{job.propertyNickname} · </span>
              ) : null}
              {job.addressSnapshot}
            </dd>
          </div>

          {job.crew.length > 1 ? (
            <div className="flex items-start gap-2">
              <dt className="sr-only">Who else</dt>
              <Users aria-hidden="true" className="mt-0.5 size-4 shrink-0 text-ink-500" />
              <dd>
                {/* Only the other people. "With Sam Cleaner" on Sam's own card
                    is noise, and a solo job needs no crew line at all. */}
                With{' '}
                {job.crew
                  .filter((member) => !member.isMe)
                  .map((member) => member.name)
                  .join(', ')}
              </dd>
            </div>
          ) : null}
        </dl>

        {photoRequiredTotal > 0 ? (
          <div className="mt-3 flex items-center gap-2 border-t border-ink-300 pt-3">
            <Camera
              aria-hidden="true"
              className={allPhotosIn ? 'size-4 text-status-completed-text' : 'size-4 text-ink-500'}
            />
            <span className="text-meta text-ink-700">
              {/* The one sentence this portal is built around. Never "0/10"
                  alone — the count means nothing without the word "photos". */}
              {photoRequiredCompleted} of {photoRequiredTotal} task photos uploaded
            </span>
          </div>
        ) : null}
      </Link>
    </li>
  );
}
